import { api, API_BASE_URL } from "../../config/api";
import { toast } from "react-toastify";
import { getAlpha2Code } from "../../lib/helper/countryToAphlaCode";

export const needsStateSelection = country => ["CA", "BR"].includes(getAlpha2Code(country));

// Unit conversion functions
const convertLbsToKg = (lbs) => {
    return lbs * 0.453592;
};

const convertInchesToCm = (inches) => {
    return inches * 2.54;
};

// Helper function to get converted weight in kg
const getWeightInKg = (weight) => {
    if (!weight || !weight.value) return 0;
    const value = parseFloat(weight.value);
    return weight.unit === 'lbs' ? convertLbsToKg(value) : value;
};

// Helper function to get converted dimension in cm
const getDimensionInCm = (dimension) => {
    if (!dimension || !dimension.value) return 0;
    const value = parseFloat(dimension.value);
    return dimension.unit === 'in' ? convertInchesToCm(value) : value;
};

export const handleInputChange = (e, setFormData, setErrors, needsStateSelection) => {
    const { name, value } = e.target || e;
    setFormData(prev => {
        let newValue = value;
        if (name === "selectedService") {
            newValue = value === "IOR&EOR" ? "EOR & IOR" : value;
            return {
                ...prev,
                [name]: newValue,
                eccnCode: newValue === "EOR" ? prev.eccnCode : null,
                requiredDoc: newValue === "EOR" ? "not_Required" : prev.requiredDoc,
            };
        }
        const newData = { ...prev, [name]: newValue };
        if (name === "originCountry" && !needsStateSelection(value)) newData.originState = "";
        if (name === "destinationCountry" && !needsStateSelection(value)) newData.destinationState = "";
        return newData;
    });
    setErrors(prev => ({ ...prev, [name]: false }));
};

export const handleDimensionChange = (index, field, e, setFormData) => {
    const { value } = e.target;
    setFormData(prev => {
        const newDimensions = [...prev.dimensions];
        if (e.target.name?.includes("unit")) {
            newDimensions[index][field] = { ...newDimensions[index][field], unit: value };
        } else {
            newDimensions[index][field] = { ...newDimensions[index][field], value };
        }
        return { ...prev, dimensions: newDimensions };
    });
};

export const handleAddDimension = (formData, setFormData) => {
    setFormData(prev => ({
        ...prev,
        dimensions: [
            ...prev.dimensions,
            {
                weight: { value: "", unit: prev.dimensions[0].weight.unit },
                length: { value: "", unit: "cm" },
                width: { value: "", unit: "cm" },
                height: { value: "", unit: "cm" },
                quantity: { value: "" },
            },
        ],
    }));
};

export const handleRemoveDimension = (index, formData, setFormData) => {
    if (formData.dimensions.length > 1) {
        setFormData(prev => ({
            ...prev,
            dimensions: prev.dimensions.filter((_, i) => i !== index),
        }));
    }
};

export const handleServiceInputChange = (index, field, e, setFormData, setErrors) => {
    const { value } = e.target;
    setFormData(prev => {
        const updatedItems = [...prev.items];
        if (field === "hasHSCode") {
            updatedItems[index].hasHSCode = value === "yes";
            if (value === "no") {
                updatedItems[index].itemHSCode = "";
                updatedItems[index].hsCodeSource = "database"; // Reset to default
            }
        } else if (field === "itemNew") {
            updatedItems[index].itemNew = value === "yes";
        } else if (field === "hsCodeSource") {
            updatedItems[index].hsCodeSource = value;
            // Clear HS Code when switching source type
            updatedItems[index].itemHSCode = "";
        } else {
            updatedItems[index][field] = value;
        }
        return { ...prev, items: updatedItems };
    });
    setErrors(prev => ({ ...prev, [`${field}-${index}`]: false }));
};

export const handleAntdServiceInputChange = (index, field, value, setFormData) => {
    setFormData(prev => {
        const updated = [...prev.items];
        updated[index][field] = value;
        return { ...prev, items: updated };
    });
};

export const handleAddMore = (setFormData) => {
    setFormData(prev => ({
        ...prev,
        items: [
            ...prev.items,
            {
                itemName: "",
                itemHSCode: "",
                itemValue: "",
                countryOrigin: "",
                quantity: "",
                hasHSCode: null,
                itemNew: true,
                hsCodeSource: "database", // Add default hsCodeSource
            },
        ],
    }));
};

export const handleRemoveService = (index, setFormData) => {
    setFormData(prev => ({
        ...prev,
        items: prev.items.filter((_, i) => i !== index),
    }));
};

export const handleInsuranceChange = (value, setFormData, totalItemsValue, setInsurance, airfreight, duty, collectionValue, setCif) => {
    setFormData(prev => ({
        ...prev,
        insuranceSelected: value,
    }));
    const calculatedInsurance = value === "YES" ? totalItemsValue * 0.0075 : 0;
    setInsurance(calculatedInsurance);
    const updatedCIF = totalItemsValue + airfreight + calculatedInsurance + duty + collectionValue;
    setCif(updatedCIF);
};

export const fetchAdminEstimate = async (loadCountry, unloadCountry, selectedTemplate, airfreightCurrency, afcWeightUnit, setAdminEstimate, setFormData, loading, setLoading, linkId) => {
    // Early return conditions to prevent unnecessary API calls
    if (!loadCountry || !unloadCountry || !selectedTemplate || !airfreightCurrency || !afcWeightUnit || loading) {
        return;
    }

    try {
        // ALWAYS use "kg" for external estimates - this is the key change
        const standardizedWeightUnit = "kg";

        // Use the new optimized endpoint with query parameters instead of fetching all estimates
        const response = await api.get(`${API_BASE_URL}/external-links/${linkId}/admin-estimates-match`, {
            params: {
                loadCountry,
                unloadCountry,
                selectedTemplate,
                airfreightCurrency,
                afcWeightUnit: standardizedWeightUnit, // Always use "kg" for external estimates
            }
        });

        const estimateData = response.data;

        if (estimateData && estimateData.length > 0) {
            // Find exact match using the same logic as before
            const exactMatch = estimateData.find(estimate =>
                (Array.isArray(estimate.loadCountry)
                    ? estimate.loadCountry.includes(loadCountry)
                    : estimate.loadCountry === loadCountry) &&
                estimate.unloadCountry === unloadCountry &&
                estimate.selectedTemplate.name === selectedTemplate &&
                estimate.afcWeightUnit === standardizedWeightUnit && // Use standardized unit
                estimate.airfreightCurrency === airfreightCurrency
            );

            if (exactMatch) {
                setAdminEstimate(exactMatch);

                // Find pending services
                const pendingServices = exactMatch.selectedTemplate.services
                    .filter(service => service.values.includes("Pending"))
                    .map(service => service.description);

                setFormData(prev => ({
                    ...prev,
                    airfreightCurrency: exactMatch.airfreightCurrency,
                    afcWeightUnit: exactMatch.afcWeightUnit,
                    selectedTemplate: exactMatch.selectedTemplate,
                    pending: pendingServices.join(", "),
                }));
            } else {
                setAdminEstimate(null);
            }
        } else {
            setAdminEstimate(null);
        }
    } catch (error) {
        console.error("Error fetching external admin estimate:", error);

        // Enhanced error handling with specific error types
        if (error.response?.status === 400) {
            toast.error("Invalid request parameters. Please check your input.");
        } else if (error.response?.status === 401) {
            toast.error("Authentication failed. Please refresh and try again.");
        } else if (error.response?.status === 403) {
            toast.error("Access denied. Your session may have expired.");
        } else if (error.response?.status === 500) {
            toast.error("Server error occurred. Please try again later.");
        } else {
            toast.error(`Failed to load admin estimate: ${error.message}`);
        }

        // Set adminEstimate to null so the calling code knows no match was found
        setAdminEstimate(null);
    }
};

export const fetchDisabledCountries = async (linkId) => {
    try {
        const response = await api.get(`${API_BASE_URL}/external-links/${linkId}/disabled-country`);
        return response.data;
    } catch (error) {
        console.error("Error fetching disabled countries:", error);
        return [];
    }
};

export const fetchEccnCodes = async (linkId) => {
    try {
        const response = await api.get(`${API_BASE_URL}/external-links/${linkId}/eccn`);
        return response.data;
    } catch (error) {
        toast.error("Failed to load ECCN codes");
        return [];
    }
};

export const calculateDHLTaxes = (dhlData, calculatedCIF, totalItemsValue, countryCode) => {
    if (!dhlData || calculatedCIF === undefined) return { dhlTaxes: [], dhlTaxTotal: 0 };

    const specialVATCountries = ["CM", "HN", "MD", "NA", "SZ", "EC", "SC", "LR", "IL", "LB", "MW", "GR"];
    const taxConfigs = {
        CM: { rate: 0.175, type: "VAT" },
        HN: { rate: 0.150, type: "SALE" },
        SZ: { rate: 0.150, type: "VAT" },
        EC: { rate: 0.150, type: "VAT" },
        MD: { rate: 0.200, type: "VAT" },
        SC: { rate: 0.150, type: "VAT" },
        LR: { rate: 0.100, type: "GST" },
        NA: { rate: 0.15, type: "VAT" },
        IL: { rate: 0.180, type: "VAT" },
        LB: { rate: 0.110, type: "VAT" },
        MW: { rate: 0.165, type: "VAT" },
        GR: { rate: 0.240, type: "VAT" },
    };

    const calculateSpecialTax = (dutyAmount, CIF, countryCode, totalItemsValue) => {
        const config = taxConfigs[countryCode];
        if (!config) return 0;
        if (countryCode === "GR") return config.rate * (CIF + dutyAmount);
        if (countryCode === "LB") {
            const addyFee = dhlData?.feeTotals.find(fee => fee.name === "ADDY")?.value || 0;
            return config.rate * (dutyAmount + CIF + addyFee);
        }
        if (countryCode === "NA") return (config.rate * totalItemsValue) * 1.1;
        if (countryCode === "EC") {
            const relevantFees = ["DUTY", "FODINFA", "ICE", "SAFEGUARD"];
            const feesTotal = dhlData?.feeTotals
                .filter(fee => relevantFees.includes(fee.name))
                .reduce((sum, fee) => sum + fee.value, 0) || 0;
            return config.rate * (totalItemsValue + feesTotal);
        }
        if (countryCode === "SZ") return config.rate * (totalItemsValue + dutyAmount);
        if (countryCode === "LR") {
            const ecowasLevy = dhlData?.feeTotals.find(fee => fee.name === "ECOWAS_TRADE_LEVY")?.value || 0;
            return config.rate * (CIF + dutyAmount + ecowasLevy);
        }
        if (countryCode === "MW") return config.rate * (dutyAmount + CIF);
        return config.rate * (dutyAmount + CIF);
    };

    const dhlTaxes = [];
    let dhlTaxTotal = 0;
    const specificTaxes = ["DUTY", "VAT", "SALE", "GST"];
    const mainTaxes = {};
    const otherTaxes = [];
    let otherTaxesTotal = 0;
    const itemHsCode = dhlData.customsDetails?.[0]?.hsCode;

    dhlData.feeTotals.forEach(fee => {
        if (fee.value <= 0) return;
        if (countryCode === "LB" && fee.name === "ACD") return;
        const shouldSkipExcise = countryCode === "MW" && itemHsCode && itemHsCode.startsWith("85");
        if (
            (fee.name === "EXCISE" && shouldSkipExcise) ||
            (countryCode === "EC" && fee.name === "SPCT") ||
            (countryCode === "DO" && fee.name === "CONS") ||
            (countryCode === "ET" && fee.name === "SWL") ||
            (countryCode === "IL" && fee.name === "TAMA")
        ) return;

        if (specificTaxes.includes(fee.name)) {
            if (
                specialVATCountries.includes(countryCode) &&
                (fee.name === "VAT" || fee.name === "SALE" || fee.name === "GST")
            ) {
                const dutyAmount = dhlData.feeTotals.find(f => f.name === "DUTY")?.value || 0;
                fee.value = calculateSpecialTax(dutyAmount, calculatedCIF, countryCode, totalItemsValue);
            }
            if (fee.value > 0) {
                mainTaxes[fee.name] = {
                    name: fee.name === "SALE" || fee.name === "GST" ? `VAT (${fee.name})` : fee.name,
                    value: fee.value,
                };
            }
        } else {
            if (countryCode === "AU" && fee.name === "IMPROC") fee.value = calculatedCIF * 0.012;
            if (fee.value > 0) {
                otherTaxes.push(fee);
                otherTaxesTotal += fee.value;
            }
        }
    });

    if (countryCode === "QA" && !otherTaxes.some(tax => tax.name === "EXTRA_TAX")) {
        const extraTaxValue = 400;
        if (extraTaxValue > 0) {
            otherTaxes.push({ name: "EXTRA_TAX", value: extraTaxValue });
            otherTaxesTotal += extraTaxValue;
        }
    }

    if (countryCode === "TR" && !otherTaxes.some(tax => tax.name === "EXTRA_TAX") && itemHsCode && itemHsCode.startsWith("8")) {
        const extraTaxValue = calculatedCIF * 0.05;
        if (extraTaxValue > 0) {
            otherTaxes.push({ name: "EXTRA_TAX", value: extraTaxValue });
            otherTaxesTotal += extraTaxValue;
        }
    }

    if (countryCode === "AU" && !otherTaxes.some(tax => tax.name === "IMPROC")) {
        const improcValue = calculatedCIF * 0.012;
        if (improcValue > 0) {
            otherTaxes.push({ name: "IMPROC", value: improcValue });
            otherTaxesTotal += improcValue;
        }
    }

    if (countryCode === "KM" && !otherTaxes.some(tax => tax.name === "ICF")) {
        const icfValue = calculatedCIF * 0.025;
        if (icfValue > 0) {
            otherTaxes.push({ name: "ICF", value: icfValue });
            otherTaxesTotal += icfValue;
        }
    }

    if (countryCode === "CR" && !otherTaxes.some(tax => tax.name === "LAW")) {
        const lawValue = calculatedCIF * 0.01;
        if (lawValue > 0) {
            otherTaxes.push({ name: "LAW", value: lawValue });
            otherTaxesTotal += lawValue;
        }
    }

    if (countryCode === "CI" && !otherTaxes.some(tax => tax.name === "EXTRA_TAX")) {
        const extraTaxValue = Math.max(calculatedCIF * 0.05, 300);
        if (extraTaxValue > 0) {
            otherTaxes.push({ name: "EXTRA_TAX", value: extraTaxValue });
            otherTaxesTotal += extraTaxValue;
        }
    }

    if (countryCode === "LB" && !otherTaxes.some(tax => tax.name === "EXTRA_TAX")) {
        const extraTaxValue = 350;
        if (extraTaxValue > 0) {
            otherTaxes.push({ name: "EXTRA_TAX", value: extraTaxValue });
            otherTaxesTotal += extraTaxValue;
        }
    }

    if (countryCode === "NA" && !mainTaxes["VAT"]) {
        const dutyAmount = dhlData.feeTotals.find(f => f.name === "DUTY")?.value || 0;
        const vatValue = calculateSpecialTax(dutyAmount, calculatedCIF, countryCode, totalItemsValue);
        if (vatValue > 0) mainTaxes["VAT"] = { name: "VAT", value: vatValue };
    }

    if (countryCode === "MW" && !mainTaxes["VAT"]) {
        const dutyAmount = dhlData.feeTotals.find(f => f.name === "DUTY")?.value || 0;
        const vatValue = calculateSpecialTax(dutyAmount, calculatedCIF, countryCode, totalItemsValue);
        if (vatValue > 0) mainTaxes["VAT"] = { name: "VAT", value: vatValue };
    }

    specificTaxes.forEach(taxName => {
        if (mainTaxes[taxName] && mainTaxes[taxName].value > 0) {
            dhlTaxes.push(mainTaxes[taxName]);
            dhlTaxTotal += mainTaxes[taxName].value;
        }
    });

    if (otherTaxes.length > 0) {
        dhlTaxes.push({ name: "Taxes", value: otherTaxesTotal, details: otherTaxes });
        dhlTaxTotal += otherTaxesTotal;
    }

    return { dhlTaxes, dhlTaxTotal };
};

export const loadHSCodeOptions = async (destinationCountry, inputValue) => {
    if (inputValue.length < 6 || !destinationCountry) return [];
    const response = await api.get(`/hscodes?country=${destinationCountry}&prefix=${inputValue.slice(0, 6)}`);
    return response.data.map(item => ({
        value: item.HS,
        label: `${item.HS} - ${item.HS4Desc} - ${item.HS6Desc}`,
    }));
};

export const renderEccnOption = (eccn) => (
    <div className="flex flex-col py-2 px-3 hover:bg-blue-50 transition-colors">
        <div className="flex justify-between items-center">
            <span className="font-semibold text-blue-600">{eccn.eccn_code}</span>
            <span className={`text-xs px-2 py-1 rounded-full ${eccn.restrictions ? "bg-red-100 text-red-600" : "bg-green-100 text-green-600"}`}>
                {eccn.restrictions ? "Restricted" : "Unrestricted"}
            </span>
        </div>
        <span className="text-sm text-gray-600 mt-1">{eccn.description}</span>
    </div>
);